import { useState, useEffect } from "react";
import { GAME } from "@/constants/testIds";
import {
  RotateCcw,
  Send,
  Home,
  Crown,
  Award,
  Flag,
  Skull,
  Crosshair,
  Coins,
  Zap,
  Flame,
} from "lucide-react";
import { getAchievementById } from "@/lib/achievements";
import { useSubmitScore } from "@/hooks/useSubmitScore";
import { devWarn } from "@/lib/devLog";

const NAME_KEY = "aobe_last_name";

function achievementIcon(iconKey, className = "w-4 h-4") {
  switch (iconKey) {
    case "flag":
      return <Flag className={className} />;
    case "skull":
      return <Skull className={className} />;
    case "crosshair":
      return <Crosshair className={className} />;
    case "coins":
      return <Coins className={className} />;
    case "zap":
      return <Zap className={className} />;
    case "flame":
    case "flame-double":
      return <Flame className={className} />;
    default:
      return <Award className={className} />;
  }
}

export default function GameOverModal({
  score,
  coins,
  distance,
  enemiesDefeated,
  bossesDefeated = 0,
  powerupsByType = { chain: 0, shades: 0, cup: 0 },
  mode = "endless",
  seedDate = null,
  onRestart,
  onExit,
}) {
  const [name, setName] = useState("");
  const { submit, submitting, submitted, rank, newAchievements } = useSubmitScore();

  useEffect(() => {
    try {
      const saved = localStorage.getItem(NAME_KEY) || "";
      setName(saved);
    } catch (e) {
      devWarn("GameOverModal: localStorage read failed", e);
    }
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    submit({
      name,
      runStats: {
        score,
        coins,
        distance,
        enemiesDefeated,
        bossesDefeated,
        powerupsByType,
        mode,
        seedDate,
      },
    });
  };

  return (
    <div
      data-testid={GAME.gameOverModal}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="relative bg-black/95 border-2 border-[var(--neon-red)] rounded-xl p-6 sm:p-8 max-w-md w-full shadow-[0_0_60px_rgba(255,0,60,0.45)] my-6">
        <Header mode={mode} seedDate={seedDate} />
        <StatsGrid
          score={score}
          distance={distance}
          coins={coins}
          enemiesDefeated={enemiesDefeated}
          bossesDefeated={bossesDefeated}
        />
        {submitted && rank != null && <RankBanner rank={rank} />}
        {newAchievements.length > 0 && (
          <NewAchievementsPanel ids={newAchievements} />
        )}
        {!submitted ? (
          <NameEntryForm
            name={name}
            setName={setName}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        ) : (
          <p className="mt-5 text-center font-hud text-[var(--crt-green)] text-lg">
            SCORE SAVED. RUN IT BACK?
          </p>
        )}
        <Actions onRestart={onRestart} onExit={onExit} />
      </div>
    </div>
  );
}

function Header({ mode, seedDate }) {
  const suffix = mode === "daily" && seedDate ? `· DAILY ${seedDate}` : "";
  return (
    <div className="text-center">
      <p className="font-hud text-[var(--crt-green)] tracking-[0.4em] text-base">
        // GAME OVER {suffix}
      </p>
      <h2 className="font-display text-5xl sm:text-6xl text-white mt-1 tracking-tight">
        YOU GOT
        <br />
        <span className="text-[var(--neon-red)]">RAN</span>
      </h2>
    </div>
  );
}

function StatsGrid({ score, distance, coins, enemiesDefeated, bossesDefeated }) {
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 font-hud">
      <Stat label="SCORE" value={String(score).padStart(6, "0")} accent />
      <Stat label="DISTANCE" value={`${distance}m`} />
      <Stat label="COINS" value={String(coins).padStart(3, "0")} />
      <Stat label="DEFEATED" value={String(enemiesDefeated)} />
      {bossesDefeated > 0 && (
        <Stat label="BOSSES" value={String(bossesDefeated)} accent />
      )}
    </div>
  );
}

function RankBanner({ rank }) {
  return (
    <div
      data-testid="rank-display"
      className="mt-5 flex items-center justify-center gap-2 font-display text-3xl text-white border-2 border-[var(--street-yellow)] rounded-lg py-3 bg-[var(--street-yellow)]/10"
    >
      {rank === 1 && <Crown className="w-7 h-7 text-[var(--street-yellow)]" />}
      <span>
        YOU RANKED <span className="text-[var(--street-yellow)]">#{rank}</span>
      </span>
    </div>
  );
}

function NewAchievementsPanel({ ids }) {
  return (
    <div
      data-testid="new-achievements"
      className="mt-4 border-2 border-[var(--crt-green)]/60 bg-[var(--crt-green)]/10 rounded-lg p-3"
    >
      <p className="font-hud text-[var(--crt-green)] text-sm tracking-[0.25em] mb-2 flex items-center gap-2">
        <Award className="w-4 h-4" /> NEW UNLOCKS
      </p>
      <ul className="space-y-1.5">
        {ids.map((id) => {
          const a = getAchievementById(id);
          if (!a) return null;
          return (
            <li
              key={id}
              className="flex items-center gap-2 font-hud text-white text-base"
              style={{ color: a.color }}
            >
              {achievementIcon(a.iconKey, "w-4 h-4")}
              <span className="tracking-wider">{a.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NameEntryForm({ name, setName, submitting, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="mt-6">
      <label
        htmlFor="player-name"
        className="block font-hud text-white/70 text-base mb-2 tracking-[0.2em]"
      >
        ENTER YOUR NAME
      </label>
      <input
        id="player-name"
        data-testid={GAME.playerNameInput}
        type="text"
        maxLength={12}
        autoFocus
        value={name}
        onChange={(e) =>
          setName(e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 12))
        }
        placeholder="EMIGO_01"
        className="w-full bg-zinc-900 border-2 border-zinc-700 focus:border-[var(--neon-red)] outline-none rounded-md font-hud text-[var(--crt-green)] text-2xl tracking-[0.2em] px-4 py-3 placeholder-zinc-600"
      />
      <button
        type="submit"
        data-testid={GAME.submitScoreBtn}
        disabled={submitting}
        className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-[var(--neon-red)] hover:bg-red-600 disabled:opacity-60 text-white font-body font-bold uppercase tracking-[0.18em] px-5 py-3 rounded-md transition-all"
      >
        <Send className="w-4 h-4" />
        {submitting ? "Saving..." : "Save Score"}
      </button>
    </form>
  );
}

function Actions({ onRestart, onExit }) {
  return (
    <div className="mt-5 grid grid-cols-2 gap-3">
      <button
        type="button"
        onClick={onRestart}
        data-testid={GAME.restartBtn}
        className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-body font-bold uppercase tracking-[0.18em] px-4 py-3 rounded-md transition-all"
      >
        <RotateCcw className="w-4 h-4" /> Restart
      </button>
      <button
        type="button"
        onClick={onExit}
        data-testid="game-over-exit-button"
        className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-body font-bold uppercase tracking-[0.18em] px-4 py-3 rounded-md transition-all"
      >
        <Home className="w-4 h-4" /> Home
      </button>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border border-white/15 bg-zinc-950 px-3 py-2">
      <div className="text-white/55 text-xs tracking-[0.25em]">{label}</div>
      <div className={`text-2xl ${accent ? "text-[var(--crt-green)] hud-glow" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
